import React, { useState, useEffect, useCallback } from 'react';

/**
 * Jobs-only alert recipients panel. Nothing to do with monitoring: one
 * checkbox per team member — do they get the daily email of new job
 * vacancies for this project (which also puts the project's jobs on
 * their dashboard). Writes through the shared recipients endpoint,
 * carrying each member's monitoring flags through untouched.
 */

interface JobsAlertRecipientsProps {
  projectId: number;
  /** Pre-tick the logged-in user (first-time setup after creating a watch). */
  autoEnableSelf?: boolean;
  currentUserId?: number | null;
}

interface RecipientRow {
  userId: number;
  email: string;
  firstName: string | null;
  lastName: string | null;
  isOwner: boolean;
  instantAlerts: boolean;
  weeklyDigest: boolean;
  jobDailyDigest: boolean;
}

const authHeaders = (): HeadersInit => ({
  'Authorization': `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json',
});

const JobsAlertRecipients: React.FC<JobsAlertRecipientsProps> = ({ projectId, autoEnableSelf, currentUserId }) => {
  const [recipients, setRecipients] = useState<RecipientRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [canManage, setCanManage] = useState(true);
  const [dirty, setDirty] = useState(false);
  const [autoAdded, setAutoAdded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/notifications/projects/${projectId}/recipients`, { headers: authHeaders() });
      if (res.status === 404) { setCanManage(false); return; }
      if (!res.ok) throw new Error('Failed to load recipients');
      const data = await res.json();
      let rows: RecipientRow[] = data.recipients || [];
      // First-time setup: pre-tick whoever is doing the setting up, if they
      // aren't a recipient yet.
      const self = rows.find(r => r.userId === currentUserId);
      if (autoEnableSelf && self && !self.jobDailyDigest) {
        rows = rows.map(r => (r.userId === currentUserId ? { ...r, jobDailyDigest: true } : r));
        setDirty(true);
        setAutoAdded(true);
      }
      setRecipients(rows);
    } catch (e: any) {
      setError(e.message || 'Failed to load recipients');
    } finally {
      setLoading(false);
    }
  }, [projectId, autoEnableSelf, currentUserId]);

  useEffect(() => { load(); }, [load]);

  const toggle = (userId: number) => {
    setRecipients(rs => rs.map(r => (r.userId === userId ? { ...r, jobDailyDigest: !r.jobDailyDigest } : r)));
    setDirty(true);
    setSaved(false);
  };

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`/api/notifications/projects/${projectId}/recipients`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({
          recipients: recipients.map(r => ({
            userId: r.userId,
            instantAlerts: r.instantAlerts,
            weeklyDigest: r.weeklyDigest,
            jobDailyDigest: r.jobDailyDigest,
          })),
        }),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Failed to save');
      setDirty(false);
      setAutoAdded(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e: any) {
      setError(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-sm text-gray-500 py-4">Loading…</p>;

  if (!canManage) {
    return (
      <p className="text-sm text-gray-600 py-2">
        Only the project owner or a team admin can change who receives job alerts.
        Ask them to add you from this screen.
      </p>
    );
  }

  return (
    <div>
      <p className="text-sm text-gray-600 mb-4">
        Ticked members get a <b>daily email</b> of new job vacancies found for this
        project, and the project's jobs appear on their dashboard.
      </p>

      {autoAdded && (
        <div className="mb-3 text-xs bg-blue-50 border border-blue-200 text-blue-800 rounded-lg px-3 py-2">
          You've been pre-selected so the alerts reach someone — click Save to confirm, and add any teammates who should get them too.
        </div>
      )}

      <div className="divide-y divide-gray-100 border border-gray-200 rounded-xl overflow-hidden">
        {recipients.map(r => (
          <label key={r.userId} className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 cursor-pointer">
            <div>
              <p className="text-sm font-medium text-gray-900">
                {[r.firstName, r.lastName].filter(Boolean).join(' ') || r.email}
                {r.isOwner && (
                  <span className="ml-2 text-[10px] font-semibold uppercase tracking-wide bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">Owner</span>
                )}
                {r.userId === currentUserId && (
                  <span className="ml-2 text-[10px] font-semibold uppercase tracking-wide bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded">You</span>
                )}
              </p>
              <p className="text-xs text-gray-500">{r.email}</p>
            </div>
            <input
              type="checkbox"
              checked={r.jobDailyDigest}
              onChange={() => toggle(r.userId)}
              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
          </label>
        ))}
      </div>

      {error && <p className="text-xs text-red-600 mt-2">{error}</p>}

      <div className="flex items-center gap-3 mt-4">
        <button
          onClick={save}
          disabled={saving || !dirty}
          className="px-4 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-40"
        >
          {saving ? 'Saving…' : 'Save recipients'}
        </button>
        {saved && <span className="text-xs text-emerald-600">Saved ✓</span>}
      </div>
    </div>
  );
};

export default JobsAlertRecipients;
